import React, { useState, useEffect } from 'react';
import Observer from '@researchgate/react-intersection-observer';

import { Spin, Modal } from 'antd';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import moment from 'moment';

import * as ToolRequest from '../../../tools/ToolRequest';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const getIoFormat = (type) => {
  let suffix;
  let min = null;
  let max = null;
  let stepSize = null;
  let formatter = v => v;
  let labelFormatter = v => v;

  switch (type) {
    case 'AIR_HUMIDITY': {
      suffix = '%';
      formatter = value => value === null ? '-' : value.toFixed(1);
      labelFormatter = value => value.toFixed(0);
      break;
    }
    case 'AIR_TEMPERATURE': {
      suffix = '°C';
      formatter = value => value === null ? '-' : value.toFixed(1);
      labelFormatter = value => value.toFixed(0);
      break;
    }
    case 'CO2_PPM': {
      suffix = 'ppm';
      formatter = value => value === null ? '-' : value.toFixed(0);
      labelFormatter = value => value.toFixed(0);
      break;
    }
    case 'EC_VALUE': {
      suffix = 'us/cm';
      formatter = value => value === null ? '-' : value.toFixed(0);
      labelFormatter = value => value.toFixed(0);
      break;
    }
    case 'PH_VALUE': {
      formatter = value => value === null ? '-' : value.toFixed(1);
      labelFormatter = value => value.toFixed(0);
      break;
    }
    case 'WATER_LEVEL':
    case 'WATER_LEVEL_DIGITAL': {
      min = 0;
      max = 2;
      stepSize = 1;
      formatter = value => ['Low', 'Normal', 'Full'][value] || '-';
      labelFormatter = value => ['Low', 'Normal', 'Full'][value];
      break;
    }
    case 'WATER_TEMPERATURE': {
      suffix = '°C';
      formatter = value => value === null ? '-' : value.toFixed(1);
      labelFormatter = value => value.toFixed(0);
      break;
    }
    case 'DISSOLVED_OXYGEN': {
      suffix = 'mg/L';
      formatter = value => value === null ? '-' : value.toFixed(2);
      labelFormatter = value => value.toFixed(0);
      break;
    }
    case 'OXYGEN_SATURATION': {
      suffix = '%';
      formatter = value => value === null ? '-' : value.toFixed(1);
      labelFormatter = value => value.toFixed(0);
      break;
    }
    case 'OXYGEN_PARTIAL_PRESSURE': {
      suffix = 'hPa';
      formatter = value => value === null ? '-' : value.toFixed(0);
      labelFormatter = value => value.toFixed(0);
      break;
    }
    case 'CURRENT': {
      suffix = 'A';
      formatter = value => value === null ? '-' : value.toFixed(2);
      labelFormatter = value => value.toFixed(2);
      break;
    }
    case 'ENERGY': {
      suffix = 'kWh';
      formatter = value => value === null ? '-' : value.toFixed(0);
      labelFormatter = value => value.toFixed(0);
      break;
    }
    case 'CAMERA': {
      return;
    }
    case 'WATER_LEAK': {
      min = 0;
      max = 1;
      stepSize = 1;
      formatter = value => ['Flood', 'Clear'][value] || '-';
      labelFormatter = value => ['Flood', 'Clear'][value];
      break;
    }
  }

  return {
    suffix,
    min,
    max,
    stepSize,
    formatter,
    labelFormatter,
  };
};

export default function StationDashboardCustomGraphInputChart(props) {
  const {
    zones,
    name,
    data,
    beginAt,
    endAt,
    selectedRange,
  } = props;

  const [chartData, setChartData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(true);

  const pointCount = moment(endAt).diff(beginAt, 'minute');
  const labels = Array.from({ length: pointCount }, (_, i) => i);

  useEffect(() => {
    if (loaded) {
      (async () => {
        const zoneIoIds = data.map(item => item.ioId);

        const scales = {};
        const datasets = [];

        await Promise.all(zoneIoIds.map(async (zoneIoId, i) => {
          const zone = zones.find(zone => zone.ios.some(io => io.id === zoneIoId));
          if (!zone) {
            return null;
          }

          const zoneIo = zone.ios.find(io => io.id === zoneIoId);
          if (!zoneIo) {
            return null;
          }

          const dataItem = data.find(item => item.ioId === zoneIoId);
          const ioName = zoneIo.name;

          const sensorRecordsRes = await ToolRequest.request('GET', '/v1/station-sensor-record-quick', {
            ioName,
            zoneId: zone.id,
            beginAt: beginAt.toISOString(),
            endAt: endAt.toISOString(),
          });

          const sensorRecordBoundaryRes = await ToolRequest.request('GET', '/v1/station-sensor-record-quick-boundary', {
            ioName,
            zoneId: zone.id,
            beginAt: beginAt.toISOString(),
            endAt: endAt.toISOString(),
          });

          const rangeDiff = endAt.valueOf() - beginAt.valueOf() - 1;

          const dataPoints = [...sensorRecordsRes.data];

          // prev & next
          if (sensorRecordsRes.data.length) {
            // prev
            if (sensorRecordBoundaryRes.prevValue !== null) {
              const prevTimeRange = beginAt.valueOf() - moment(sensorRecordBoundaryRes.prevCreatedAt).valueOf() + sensorRecordsRes.data[0].timeOffset;
              const valueProportional = (sensorRecordBoundaryRes.prevValue - sensorRecordsRes.data[0].value) * sensorRecordsRes.data[0].timeOffset / prevTimeRange + sensorRecordsRes.data[0].value;

              dataPoints.unshift({
                timeOffset: 0,
                value: valueProportional,
              });
            } else {
              dataPoints.unshift({
                timeOffset: 0,
                value: 0,
              });
            }

            // next
            if (sensorRecordBoundaryRes.nextValue !== null) {
              const nextTimeRange = moment(sensorRecordBoundaryRes.nextCreatedAt).valueOf() - (beginAt.valueOf() + sensorRecordsRes.data[sensorRecordsRes.data.length - 1].timeOffset);
              const valueProportional = (sensorRecordBoundaryRes.nextValue - sensorRecordsRes.data[sensorRecordsRes.data.length - 1].value) * (rangeDiff - sensorRecordsRes.data[sensorRecordsRes.data.length - 1].timeOffset) / nextTimeRange + sensorRecordsRes.data[sensorRecordsRes.data.length - 1].value;

              dataPoints.push({
                timeOffset: rangeDiff,
                value: sensorRecordBoundaryRes.nextValue,
              });
            } else {
              dataPoints.push({
                timeOffset: rangeDiff,
                value: sensorRecordsRes.data[sensorRecordsRes.data.length - 1].value,
              });
            }
          } else {
            // only prev
            if (sensorRecordBoundaryRes.prevValue !== null) {
              dataPoints.push({
                timeOffset: 0,
                value: sensorRecordBoundaryRes.prevValue,
              });
              dataPoints.push({
                timeOffset: rangeDiff,
                value: sensorRecordBoundaryRes.prevValue,
              });
            }
          }

          // datasets
          const seriesData = new Array(pointCount).fill(null);
          for (let dataPoint of dataPoints) {
            const index = Math.floor(dataPoint.timeOffset / 60000);    // every 1 min
            seriesData[index] = dataPoint.value;
          }

          const ioFormat = getIoFormat(zoneIo.type);

          const ticks = {
            stepSize: ioFormat.stepSize,
            callback: ioFormat.labelFormatter,
          };

          datasets[i] = {
            label: ioName,
            data: seriesData,
            borderColor: dataItem.color,
            backgroundColor: dataItem.color,
            yAxisID: zoneIoId,
            pointRadius: 0,
            tension: 0.35,
            borderWidth: 1,
          };

          // scales
          scales[zoneIoId] = {
            type: 'linear',
            display: true,
            position: i % 2 ? 'right' : 'left',
            ...(i > 0 ? {
              grid: {
                drawOnChartArea: false
              },
            } : {}),
            title: {
              display: true,
              text: `${ioName}${ioFormat.suffix ? ` (${ioFormat.suffix})` : ''}`,
            },
            min: ioFormat.min === null ? dataItem.min : ioFormat.min,
            max: ioFormat.max === null ? dataItem.max : ioFormat.max,
            ticks,
          };
        }));

        setChartData({
          data: {
            labels,
            datasets,
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            spanGaps: true,
            interaction: {
              mode: 'index',
              intersect: false,
            },
            stacked: false,
            plugins: {
              backgroundPlugin: true,
              customCanvasBackgroundColor: {
                color: '#fff',
              },
              tooltip: {
                callbacks: {
                  title: (tooltipItems) => {
                    return moment(beginAt).add(tooltipItems[0].dataIndex * 60000).format('YYYY-MM-DD HH:mm:ss');
                  },
                  label: (tooltipItem) => {
                    const zone = zones.find(zone => zone.ios.some(io => io.id === tooltipItem.dataset.yAxisID));
                    const zoneIo = zone.ios.find(io => io.id === tooltipItem.dataset.yAxisID);
                    const ioFormat = getIoFormat(zoneIo.type);

                    const value = ioFormat.formatter(tooltipItem.raw);
                    return `${tooltipItem.dataset.label}: ${value}`;
                  },
                },
              },
            },
            scales: {
              ...scales,
              x: {
                ticks: {
                  autoSkip: true,
                  maxTicksLimit: 8,
                  maxRotation: 0,
                  callback: (value) => {
                    return moment(beginAt).add(value * 60000).format('YYYY-MM-DD HH:mm:ss');
                  },
                },
                grid: {
                  display: false,
                },
              },
            },
          },
        });

        setLoading(false);
      })();
    }
  }, [loaded]);

  return (
    <Spin spinning={loading}>
      <Observer
        onChange={(event) => {
          if (event.isIntersecting) {
            setLoaded(true);
          }
        }}
      >
        <div style={{ width: '100%', height: 400 }}>
          {chartData && (
            <Line
              options={chartData.options}
              data={chartData.data}
              plugins={[
                {
                  id: 'backgroundPlugin',
                  beforeDraw: (chart) => {
                    const { ctx, width, height } = chart;
                    ctx.save();
                    ctx.fillStyle = 'white';
                    ctx.fillRect(0, 0, width, height);
                    ctx.restore();
                  },
                },
              ]}
            />
          )}
        </div>
      </Observer>
    </Spin>
  );
};
